import React, { useState, useEffect, useRef, useCallback } from 'react';
import { periodoService, Periodo, horarioService, Horario, apiServices, Turma } from '../services/api';
import api from '../services/api';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import './Periodos.css';

const Periodos: React.FC = () => {
  const [periodos, setPeriodos] = useState<Periodo[]>([]);
  const [horarios, setHorarios] = useState<Horario[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingHorarios, setLoadingHorarios] = useState(false);
  const [savingPeriodo, setSavingPeriodo] = useState(false);
  const [savingHorario, setSavingHorario] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showHorarioForm, setShowHorarioForm] = useState(false);
  const [selectedPeriodoId, setSelectedPeriodoId] = useState<string | null>(null);
  const [editingPeriodo, setEditingPeriodo] = useState<Periodo | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    itemName: string;
    onConfirm: () => void;
    isLoading: boolean;
    turmasVinculadas?: string[];
  }>({
    isOpen: false,
    title: '',
    message: '',
    itemName: '',
    onConfirm: () => {},
    isLoading: false
  });
  const [deletingPeriodo, setDeletingPeriodo] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    nome: ''
  });

  const diasSemana = [
    { value: 1, label: 'Segunda' },
    { value: 2, label: 'Terça' },
    { value: 3, label: 'Quarta' },
    { value: 4, label: 'Quinta' },
    { value: 5, label: 'Sexta' },
    { value: 6, label: 'Sábado' },
    { value: 7, label: 'Domingo' }
  ];

  const [horarioFormData, setHorarioFormData] = useState({
    diasSemana: [] as number[],
    horaInicio: '',
    horaFim: ''
  });

  // Refs para navegação TAB melhorada
  const horaInicioRef = useRef<HTMLInputElement>(null);
  const horaFimRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadPeriodos();
  }, []);



  const loadPeriodos = async () => {
    try {
      setLoading(true);
      const data = await periodoService.getAll();
      setPeriodos(data);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar períodos');
      console.error('Erro ao carregar períodos:', err);
    } finally {
      setLoading(false);
    }
  };

  // Função para ordenar horários por dia da semana e posição no dia
  const sortHorarios = (horarios: Horario[]) => {
    return horarios.sort((a, b) => {
      // Primeiro ordena por dia da semana
      if (a.diaSemana !== b.diaSemana) {
        return a.diaSemana - b.diaSemana;
      }
      // Depois ordena por posição no dia (se disponível)
      if (a.posicaoDia && b.posicaoDia) {
        return a.posicaoDia - b.posicaoDia;
      }
      // Se não tiver posicaoDia, ordena por hora de início
      const horaA = a.horaInicio || a.inicio || '00:00';
      const horaB = b.horaInicio || b.inicio || '00:00';
      return horaA.localeCompare(horaB);
    });
  };

  const loadHorarios = useCallback(async (periodoId: string) => {
    try {
      setLoadingHorarios(true);
      const data = await apiServices.getHorariosByPeriodo(periodoId);
      // Ordenar horários antes de definir no estado
      const horariosOrdenados = sortHorarios(data);
      setHorarios(horariosOrdenados);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar horários');
      console.error('Erro ao carregar horários:', err);
    } finally {
      setLoadingHorarios(false);
    }
  }, []);

  useEffect(() => {
    if (selectedPeriodoId) {
      loadHorarios(selectedPeriodoId);
    }
  }, [selectedPeriodoId, loadHorarios]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingPeriodo(true);
    try {
      if (editingPeriodo && editingPeriodo.id) {
        await periodoService.update(editingPeriodo.id, formData);
        setSuccess('Período atualizado com sucesso!');
      } else {
        await periodoService.create(formData);
        setSuccess('Período criado com sucesso!');
      }
      
      setFormData({ nome: '' });
      setShowForm(false);
      setEditingPeriodo(null);
      loadPeriodos();
      setError(null);
    } catch (err) {
      setError('Erro ao salvar período');
      console.error('Erro ao salvar período:', err);
    } finally {
      setSavingPeriodo(false);
    }
  };

  const handleEdit = (periodo: Periodo) => {
    setEditingPeriodo(periodo);
    setFormData({
      nome: periodo.nome
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    const periodo = periodos.find(p => p.id === id);
    setDeletingPeriodo(id);
    
    try {
      // Verificar se o período possui turmas vinculadas
      const response = await api.get(`/Turma/Listar`);
      const todasTurmas: Turma[] = response.data;
      const turmasVinculadas = todasTurmas.filter(turma => turma.periodo?.id === id);
      
      let message = 'Tem certeza que deseja excluir este período?';
      
      if (turmasVinculadas.length > 0) {
        const nomesTurmas = turmasVinculadas.map(turma => turma.nome).join(', ');
        message = `Esse período possui turmas vinculadas, elas também serão excluídas no processo, as turmas são: ${nomesTurmas}.`;
      }
      
      setDeleteModal({
        isOpen: true,
        title: 'Excluir Período',
        message,
        itemName: periodo?.nome || '',
        onConfirm: () => confirmDeletePeriodo(id),
        isLoading: false,
        turmasVinculadas: turmasVinculadas.length > 0 ? turmasVinculadas.map(t => t.nome) : undefined
      });
    } catch (err) {
      setError('Erro ao verificar turmas vinculadas ao período');
      console.error('Erro ao verificar turmas:', err);
    } finally {
      setDeletingPeriodo(null);
    }
  };

  const confirmDeletePeriodo = async (id: string) => {
    setDeleteModal(prev => ({ ...prev, isLoading: true }));
    try {
      await periodoService.delete(id);
      setSuccess('Período excluído com sucesso!');
      loadPeriodos();
      setError(null);
      setDeleteModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
      
      // Se o período excluído estava selecionado, voltar para a lista
      if (selectedPeriodoId === id) {
        setSelectedPeriodoId(null);
        setHorarios([]);
      }
    } catch (err) {
      setError('Erro ao excluir período');
      console.error('Erro ao excluir período:', err);
      setDeleteModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  const filteredPeriodos = periodos.filter(periodo =>
    periodo.nome.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const resetForm = () => {
    setFormData({ nome: '' });
    setShowForm(false);
    setEditingPeriodo(null);
  };

  const handleHorarioSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPeriodoId) return;
    
    setSavingHorario(true);
    try {
      {
        // Criação de horários múltiplos usando o endpoint de cadastrar
        const horariosData = horarioFormData.diasSemana.map(dia => ({
          diaSemana: dia, // Agora dia já é um número
          inicio: horarioFormData.horaInicio + ':00',
          fim: horarioFormData.horaFim + ':00'
        }));
        
        await apiServices.cadastrarHorarios(selectedPeriodoId, horariosData);
      }
      
      setSuccess('Horários criados com sucesso!');
      // Não resetar o formulário para permitir adicionar mais horários
      // Copiar hora fim para hora início e limpar hora fim para facilitar horários sequenciais
      // Manter a seleção dos dias da semana
      setHorarioFormData(prev => ({ 
        diasSemana: prev.diasSemana, 
        horaInicio: prev.horaFim,
        horaFim: ''
      }));
      // Recarregar e reorganizar horários após inserção
      await loadHorarios(selectedPeriodoId);
      setError(null);
      
      // Focar no campo hora início após salvar
      setTimeout(() => {
        horaInicioRef.current?.focus();
      }, 100);
    } catch (err) {
      setError('Erro ao salvar horário');
      console.error('Erro ao salvar horário:', err);
    } finally {
      setSavingHorario(false);
    }
  };



  const handleDeleteHorario = (id: number | string) => {
    const horario = horarios.find(h => h.id === id);
    const horarioText = horario ? `${horario.inicio?.substring(0, 5) || ''} - ${horario.fim?.substring(0, 5) || ''}` : '';
    setDeleteModal({
      isOpen: true,
      title: 'Excluir Horário',
      message: 'Tem certeza que deseja excluir este horário?',
      itemName: horarioText,
      onConfirm: () => confirmDeleteHorario(id),
      isLoading: false
    });
  };

  const confirmDeleteHorario = async (id: number | string) => {
    setDeleteModal(prev => ({ ...prev, isLoading: true }));
    try {
      await horarioService.delete(id);
      setSuccess('Horário excluído com sucesso!');
      if (selectedPeriodoId) {
        loadHorarios(selectedPeriodoId);
      }
      setError(null);
      setDeleteModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
    } catch (err) {
      setError('Erro ao excluir horário');
      console.error('Erro ao excluir horário:', err);
      setDeleteModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  const resetHorarioForm = () => {
    setHorarioFormData({ diasSemana: [], horaInicio: '', horaFim: '' });
    setShowHorarioForm(false);
  };

  const handleDiaToggle = (dia: number) => {
    setHorarioFormData(prev => ({
      ...prev,
      diasSemana: prev.diasSemana.includes(dia)
        ? prev.diasSemana.filter(d => d !== dia)
        : [...prev.diasSemana, dia]
    }));
  };

  const handleSelectPeriodo = (periodo: Periodo) => {
    if (periodo.id) {
      setSelectedPeriodoId(periodo.id);
      setShowForm(false);
      setShowHorarioForm(false);
    }
  };

  const handleBackToPeriodos = () => {
    setSelectedPeriodoId(null);
    setHorarios([]);
    setShowHorarioForm(false);
  };

  return (
    <div className="main-content">
      <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div className="flex-grow-1">
              <h2 className="mb-2 text-start">
                <i className="fas fa-clock me-2"></i>
                {selectedPeriodoId ? 'Gerenciar Horários' : 'Períodos e Horários'}
              </h2>
              {selectedPeriodoId && (
                <p className="text-muted mb-0">
                  Período: {periodos.find(p => p.id === selectedPeriodoId)?.nome}
                </p>
              )}
            </div>
            <div className="d-flex gap-2">
              {selectedPeriodoId ? (
                <>
                  <button
                    className="btn btn-success"
                    onClick={() => setShowHorarioForm(true)}
                  >
                    <i className="fas fa-plus me-2"></i>
                    Novo Horário
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={handleBackToPeriodos}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    Voltar aos Períodos
                  </button>
                </>
              ) : (
                <button
                  className="btn btn-success"
                  onClick={() => setShowForm(true)}
                >
                  <i className="fas fa-plus me-2"></i>
                  Novo Período
                </button>
              )}
            </div>
          </div>

          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError(null)}
              ></button>
            </div>
          )}

          {success && (
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              {success}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccess(null)}
              ></button>
            </div>
          )}

          {showForm && (
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="fas fa-edit me-2"></i>
                  {editingPeriodo ? 'Editar Período' : 'Novo Período'}
                </h5>
              </div>
              <div className="card-body p-4">
                <form onSubmit={handleSubmit}>
                  <div className="row mb-4">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="nome" className="form-label fw-semibold">
                        Nome do Período *
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-lg"
                        id="nome"
                        value={formData.nome}
                        onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                        placeholder="Ex: 1º Período, 2º Período, Matutino..."
                        maxLength={30}
                        style={{
                          borderRadius: '8px',
                          border: '2px solid #e9ecef',
                          transition: 'all 0.2s ease'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#007bff';
                          e.target.style.boxShadow = '0 0 0 0.2rem rgba(0,123,255,0.25)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e9ecef';
                          e.target.style.boxShadow = 'none';
                        }}
                        required
                       />
                       <div className="d-flex gap-3 justify-content-end mt-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary px-4 py-2"
                      onClick={resetForm}
                      style={{
                        borderRadius: '8px',
                        fontWeight: '500',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <i className="fas fa-times me-2"></i>
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="btn btn-success px-4 py-2"
                      disabled={savingPeriodo}
                      style={{
                        borderRadius: '8px',
                        fontWeight: '500',
                        minWidth: '120px'
                      }}
                    >
                      {savingPeriodo ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Salvando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          {editingPeriodo ? 'Atualizar' : 'Cadastrar'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
                </form>
              </div>
            </div>
          )}

          {showHorarioForm && selectedPeriodoId && (
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="fas fa-clock me-2"></i>
                  Novo Horário
                </h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleHorarioSubmit}>
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">Dias da Semana *</label>
                        <div className="d-flex flex-wrap gap-2">
                          {diasSemana.map((dia) => (
                            <button
                              key={dia.value}
                              type="button"
                              className={`btn btn-sm ${
                                horarioFormData.diasSemana.includes(dia.value)
                                  ? 'btn-primary'
                                  : 'btn-outline-primary'
                              }`}
                              onClick={() => handleDiaToggle(dia.value)}
                            >
                              {dia.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-4">
                        <label htmlFor="horaInicio" className="form-label fw-semibold">
                          Hora de Início *
                        </label>
                        <input
                          ref={horaInicioRef}
                          type="time"
                          className="form-control form-control-lg"
                          id="horaInicio"
                          value={horarioFormData.horaInicio}
                          onChange={(e) => setHorarioFormData({ ...horarioFormData, horaInicio: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Tab' && !e.shiftKey) {
                              e.preventDefault();
                              horaFimRef.current?.focus();
                            }
                          }}
                          style={{
                            borderRadius: '8px',
                            border: '2px solid #e9ecef',
                            transition: 'all 0.2s ease'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#0d6efd';
                            e.target.style.boxShadow = '0 0 0 0.2rem rgba(13, 110, 253, 0.25)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e9ecef';
                            e.target.style.boxShadow = 'none';
                          }}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-4">
                        <label htmlFor="horaFim" className="form-label fw-semibold">
                          Hora de Fim *
                        </label>
                        <input
                          ref={horaFimRef}
                          type="time"
                          className="form-control form-control-lg"
                          id="horaFim"
                          value={horarioFormData.horaFim}
                          onChange={(e) => setHorarioFormData({ ...horarioFormData, horaFim: e.target.value })}
                          style={{
                            borderRadius: '8px',
                            border: '2px solid #e9ecef',
                            transition: 'all 0.2s ease'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#0d6efd';
                            e.target.style.boxShadow = '0 0 0 0.2rem rgba(13, 110, 253, 0.25)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e9ecef';
                            e.target.style.boxShadow = 'none';
                          }}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="d-flex gap-3 justify-content-end mt-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary px-4 py-2"
                      onClick={resetHorarioForm}
                      style={{
                        borderRadius: '8px',
                        fontWeight: '500',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <i className="fas fa-times me-2"></i>
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="btn btn-success px-4 py-2"
                      disabled={savingHorario}
                      style={{
                        borderRadius: '8px',
                        fontWeight: '500',
                        transition: 'all 0.2s ease',
                        minWidth: '120px'
                      }}
                    >
                      {savingHorario ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Salvando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Salvar
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {!selectedPeriodoId ? (
            <div className="card">
              <div className="card-header">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <h5 className="mb-0 text-center">
                      <i className="fas fa-list me-2"></i>
                      Períodos Cadastrados
                    </h5>
                  </div>
                  <div className="col-md-6">
                    <div className="input-group" style={{ width: '300px', marginLeft: 'auto' }}>
                      <span className="input-group-text">
                        <i className="fas fa-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Buscar período..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-body">
                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                    <p className="mt-2 text-muted">Carregando períodos...</p>
                  </div>
                ) : filteredPeriodos.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="fas fa-clock fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">Nenhum período encontrado</h5>
                    <p className="text-muted">Comece criando um novo período.</p>
                  </div>
                ) : (
                  <div className="row">
                    {filteredPeriodos.map((periodo) => (
                      <div key={periodo.id} className="col-xl-3 col-lg-4 col-md-6 col-12 mb-3">
                        <div className="turma-card" style={{ cursor: 'pointer' }} onClick={() => handleSelectPeriodo(periodo)}>
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                              <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '50px', height: '50px' }}>
                                <i className="fas fa-clock text-white"></i>
                              </div>
                              <div>
                                <h6 className="mb-0">{periodo.nome}</h6>
                                <small className="text-muted">Clique para gerenciar horários</small>
                              </div>
                            </div>
                            

                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-columns me-2"></i>
                    <h5 className="mb-0">Horários por Dia da Semana</h5>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div className="badge bg-primary fs-6">{horarios.length} horários</div>
                    <button
                      className="btn btn-success"
                      style={{ backgroundColor: 'rgb(13, 110, 253)', borderColor: 'rgb(13, 110, 253)' }}
                      onClick={() => {
                        const periodo = periodos.find(p => p.id === selectedPeriodoId);
                        if (periodo) handleEdit(periodo);
                      }}
                    >
                      <i className="fas fa-edit me-2"></i>
                      Editar Período
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => {
                        if (selectedPeriodoId) handleDelete(selectedPeriodoId);
                      }}
                      disabled={deletingPeriodo === selectedPeriodoId}
                    >
                      {deletingPeriodo === selectedPeriodoId ? (
                        <>
                          <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">Carregando...</span>
                          </div>
                          Verificando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-trash me-2"></i>
                          Excluir Período
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
              <div className="card-body p-3">
                {loadingHorarios ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                    <p className="mt-2 text-muted">Carregando horários...</p>
                  </div>
                ) : horarios.length > 0 ? (
                  <div className="row g-3">
                    {diasSemana.filter((dia) => {
                      // Sempre mostrar segunda a sexta (1-5)
                      if (dia.value <= 5) return true;
                      // Mostrar sábado e domingo apenas se houver horários
                      const horariosNoDia = horarios.filter(h => h.diaSemana === dia.value);
                      return horariosNoDia.length > 0;
                    }).map((dia) => {
                      const horariosNoDia = horarios.filter(h => h.diaSemana === dia.value);
                      return (
                        <div key={dia.value} className="col-lg-2 col-md-4 col-sm-6 col-12">
                          <div className="card h-100 border-0 shadow-sm">
                            <div className={`card-header text-white text-center py-2 ${
                              dia.value <= 5 ? 'bg-primary' : 'bg-secondary'
                            }`}>
                              <h6 className="mb-0 fw-bold">
                                <i className="fas fa-calendar-day me-1"></i>
                                {dia.label}
                              </h6>
                            </div>
                            <div className="card-body p-2">
                              {horariosNoDia.length > 0 ? (
                                <div className="d-flex flex-column gap-2">
                                  {horariosNoDia.map((horario) => {
                                    // Calcular duração
                                     let duracaoTexto = '--';
                                     const horaInicioField = horario.horaInicio || horario.inicio;
                                     const horaFimField = horario.horaFim || horario.fim;
                                     if (horaInicioField && horaFimField) {
                                       const inicio = new Date(`2000-01-01T${horaInicioField}`);
                                       const fim = new Date(`2000-01-01T${horaFimField}`);
                                       const duracaoMs = fim.getTime() - inicio.getTime();
                                       const duracao = new Date(duracaoMs);
                                       const horas = duracao.getUTCHours();
                                       const minutos = duracao.getUTCMinutes();
                                       duracaoTexto = `${horas}h${minutos > 0 ? ` ${minutos}min` : ''}`;
                                     }
                                    
                                    return (
                                      <div key={horario.id} className="border rounded p-2 bg-light position-relative">
                                        <div className="position-absolute top-0 end-0 p-1">
                                          <button 
                                            className="btn btn-sm btn-outline-danger" 
                                            onClick={() => horario.id && handleDeleteHorario(horario.id)}
                                            title="Excluir horário"
                                            style={{fontSize: '12px', padding: '2px 6px'}}
                                          >
                                            <i className="fas fa-trash"></i>
                                          </button>
                                        </div>
                                        <div className="pe-4">
                                          <div className="d-flex align-items-center mb-1">
                                            <i className="fas fa-clock text-primary me-1" style={{fontSize: '12px'}}></i>
                                            <strong className="text-primary" style={{fontSize: '13px'}}>
                                              {(horario.horaInicio || horario.inicio)?.substring(0, 5) || '--:--'} - {(horario.horaFim || horario.fim)?.substring(0, 5) || '--:--'}
                                            </strong>
                                          </div>
                                          <div className="d-flex align-items-center">
                                            <i className="fas fa-stopwatch text-muted me-1" style={{fontSize: '11px'}}></i>
                                            <small className="text-muted">{duracaoTexto}</small>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="text-center text-muted py-3">
                                  <i className="fas fa-calendar-times mb-2 d-block" style={{fontSize: '24px', opacity: 0.5}}></i>
                                  <small>Sem horários</small>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-5 text-muted">
                    <i className="fas fa-calendar-times fa-3x mb-3"></i>
                    <p className="h5">Nenhum horário cadastrado</p>
                    <p>Adicione o primeiro horário usando o formulário acima</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Modal de Confirmação */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        title={deleteModal.title}
        message={deleteModal.message}
        itemName={deleteModal.itemName}
        onConfirm={deleteModal.onConfirm}
        onClose={() => setDeleteModal(prev => ({ ...prev, isOpen: false }))}
        isLoading={deleteModal.isLoading}
        turmasVinculadas={deleteModal.turmasVinculadas}
      />
      </div>
    </div>
  );
};

export default Periodos;